import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBusinessAuthContext } from "./useBusinessAuthContext";
import BusinessMenu from "./BusinessMenu";
import jsPDF from "jspdf";
import sidebar from "../../images/svg/sidebar.svg";
import setting from "../../images/svg/settings.svg";
import signed from "../../images/svg/signed.svg";
import waivers from "../../images/svg/waivers.svg";
import dashboard from "../../images/svg/dashboard.svg";
import billing from "../../images/svg/billing.svg";
import getQr from "../../images/svg/getQr.svg";
import eye from "../../images/svg/eye-black.svg";
import create from "../../images/svg/create-add.svg";
import logo from "../../images/svg/sidebar-logo.svg";
import icon1 from "../../images/svg/signature-green.svg";
import icon2 from "../../images/svg/signature-blue.svg";
import icon3 from "../../images/svg/activeWave-orange.svg";
import quick from "../../images/svg/clock-rewind.svg";
import qr from "../../images/svg/qr.svg";
import ra from "../../images/svg/recent-activity.svg";
import smallLogo from "../../images/svg/mini-logo.svg";
import Sidebar from "../../components/Sidebar";


import API_URL from "../../api/API";
import RecentActivityRow from "../../components/recentactivity/RecentActivityRow";


const BusinessDashboard = () => {
  
  const { business } = useBusinessAuthContext();
  const navigate = useNavigate();

  const [recentActivities, setRecentActivities] = useState([]);
  const [recentActivityLoading, setRecentActivityLoading] = useState(true);
  const [recentActivityError, setRecentActivityError] = useState(null);

  const [signatureStats, setSignatureStats] = useState({
      today: null,
      last7Days: null,
      activeWaivers: null,
  });

  const [signatureStatsLoading, setSignatureStatsLoading] =
      useState(true);
  
  useEffect(() => {
      if (!business) return;

      let cancelled = false;

      const fetchSignatureStats = async () => {
          setSignatureStatsLoading(true);

          try {
              const headers = {
                  Authorization: `Bearer ${business.token}`,
                  "x-session-id": business.sessionId,
                  "x-frontend-url": window.location.pathname,
              };

              const [
                  todayRes,
                  sevenDayRes,
                  activeWaiverRes,
              ] = await Promise.all([
                  fetch(
                      `${API_URL.BUSINESS_WAIVER_SIGNATURE_COUNT}?range=today`,
                      { headers }
                  ),
                  fetch(
                      `${API_URL.BUSINESS_WAIVER_SIGNATURE_COUNT}?range=7days`,
                      { headers }
                  ),
                  fetch(
                      API_URL.BUSINESS_ACTIVE_WAIVER_COUNT,
                      { headers }
                  ),
              ]);

              if (
                  !todayRes.ok ||
                  !sevenDayRes.ok ||
                  !activeWaiverRes.ok
              ) {
                  throw new Error("Failed to load dashboard stats");
              }

              const [
                  todayData,
                  sevenDayData,
                  activeWaiverData,
              ] = await Promise.all([
                  todayRes.json(),
                  sevenDayRes.json(),
                  activeWaiverRes.json(),
              ]);

              if (
                  !todayData.success ||
                  !sevenDayData.success ||
                  !activeWaiverData.success
              ) {
                  throw new Error("Failed to load dashboard stats");
              }

              if (!cancelled) {
                  setSignatureStats({
                      today: todayData.count,
                      last7Days: sevenDayData.count,
                      activeWaivers: activeWaiverData.count,
                  });
              }
          } catch (error) {
              console.error(
                  "Failed to load signature stats:",
                  error
              );
          } finally {
              if (!cancelled) {
                  setSignatureStatsLoading(false);
              }
          }
      };

      fetchSignatureStats();

      return () => {
          cancelled = true;
      };
  }, [business]);


  
  useEffect(() => {
   
    if (!business) {
      return;
    }

    const fetchRecentActivities = async () => {
      try {
        setRecentActivityLoading(true);
        setRecentActivityError(null);

        const url =
          `${API_URL.BUSINESS_GET_RECENT_ACTIVITY}` +
          `?page=1&limit=4`;

        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${business.token}`,
            "x-session-id": business.sessionId,
            "x-frontend-url": window.location.pathname,
          },
        });

        const data = await res.json();

        if (!res.ok || !data.success) {
          throw new Error(
            data.message || "Failed to load recent activity"
          );
        }

        setRecentActivities(data.activities || []);

      } catch (err) {
        console.error(
          "Dashboard recent activity error:",
          err
        );

        setRecentActivityError(err.message);
      } finally {
        setRecentActivityLoading(false);
      }
    };

    fetchRecentActivities();

  }, [business]);


  if (!business) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600 text-lg">
        Not logged in
      </div>
    );
  }

  /*// QR code URLs - may move to new page
  const qrCodeImage = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(business.qrCodeUrl)}`;
  const qrCodeHighRes = `https://api.qrserver.com/v1/create-qr-code/?size=1000x1000&data=${encodeURIComponent(business.qrCodeUrl)}`;

  // Download QR code in high resolution
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = qrCodeHighRes;
    link.download = `${business.businessName}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();

    // Title (Business Name)
    doc.setFontSize(22);
    doc.text(business.businessName, pageWidth / 2, 80, { align: "center" });

    // Subtitle
    doc.setFontSize(14);
    doc.text("Digital Waiver QR Code", pageWidth / 2, 110, { align: "center" });

    // Add QR Code (centered)
    const qrSize = 200;
    const qrX = (pageWidth - qrSize) / 2; // center horizontally
    doc.addImage(qrCodeHighRes, "PNG", qrX, 140, qrSize, qrSize);

    // Business URL
    doc.setFontSize(12);
    doc.textWithLink(business.qrCodeUrl, pageWidth / 2, 370, {
      url: business.qrCodeUrl,
      align: "center",
    });

    // Save PDF
    doc.save(`${business.businessName}-qr.pdf`);
  };*/


  return (
    <div className="h-screen flex flex-row bg-background">      

      <Sidebar />

      <div className="flex-1 h-auto overflow-auto py-9 transition">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
          {/* Header */}
          <div className="mb-5 md:mb-6">
            <h1 className="text-2xl font-medium text-darkLighted mb-2">
              Dashboard
            </h1>
            <p className="text-sm md:text-base font-normal text-foreground">
              Welcome back — here’s what’s happening today.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {/* Stats Cards */}
            <div className="grid lg:grid-cols-3 gap-4">
              <div 
              onClick={() =>
                navigate("/business/waivers/signatures?range=today")
              }              
              className="bg-white border border-stroked rounded-2xl p-4 md:p-6 flex items-center gap-3">
                <div className="size-11 md:size-[50px] flex-none rounded-lg bg-[#EBF5EA] flex justify-center items-center">
                  <img src={icon1} alt="icon1" className="size-6" />
                </div>
                <div className="space-y-1">
                  <p className="text-foreground text-sm font-normal capitalize">
                    Signatures Today
                  </p>
                  <p className="text-darkLighted text-xl md:text-2xl font-medium leading-none">
                    {signatureStatsLoading
                    ? "Loading..."
                    : signatureStats.today ?? "—"}
                  </p>
                </div>
              </div>

              <div 
                onClick={() =>
                  navigate("/business/waivers/signatures?range=7days")
                }                
                className="bg-white border border-stroked rounded-2xl p-4 md:p-6 flex items-center gap-3">
                <div className="size-11 md:size-[50px] flex-none rounded-lg bg-[#E6F3FC] flex justify-center items-center">
                  <img src={icon2} alt="icon2" className="size-6" />
                </div>
                <div className="space-y-1">
                  <p className="text-foreground text-sm font-normal capitalize">
                    Signatures (Last 7 Days)
                  </p>
                  <p className="text-darkLighted text-xl md:text-2xl font-medium leading-none">
                    {signatureStatsLoading
                    ? "Loading..."
                    : signatureStats.last7Days ?? "—"}
                  </p>
                </div>
              </div>

              <div 
                onClick={() =>
                  navigate("/business/waivers")
                }              
              
                className="bg-white border border-stroked rounded-2xl p-4 md:p-6 flex items-center gap-3">
                <div className="size-11 md:size-[50px] flex-none rounded-lg bg-[#FCF3E2] flex justify-center items-center">
                  <img src={icon3} alt="icon3" className="size-6" />
                </div>
                <div className="space-y-1">
                  <p className="text-foreground text-sm font-normal capitalize">
                    Active Waivers
                  </p>
                  <p className="text-darkLighted text-xl md:text-2xl font-medium leading-none">
                    {signatureStatsLoading
                    ? "Loading..."
                    : signatureStats.activeWaivers ?? "—"}
                    </p>
                </div>
              </div>


            </div>

            {/* Quick Actions */}
            <div className="bg-white border border-stroked rounded-2xl p-4 xs:p-5 md:p-6 space-y-4 md:space-y-6">
              <div className="flex items-start gap-3">
                <div className="flex-none size-10 bg-[#F7F7F5] rounded-14 flex justify-center items-center">
                  <img src={quick} alt="quick" className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-darkLighted mb-1">
                    Quick Actions
                  </h3>
                  <p className="font-normal text-sm text-foreground">
                    Get waivers signed faster with QR codes.
                  </p>
                </div>
              </div>

              {/* Get QR Code 
              <button
                onClick={() => setShowQRModal(true)}
                className="w-full bg-highlited rounded-full inline-flex justify-center items-center gap-3 max-md:text-sm text-black font-medium p-3 md:p-4 hover:bg-[#D5AA00] transition mb-4"
              >
                <img src={getQr} alt="getQr" className="w-5 mt-0.5" />
                Get QR Code
              </button>*/}
              <button
                onClick={() => navigate("/business/qr-codes")}
                className="w-full bg-highlited rounded-full inline-flex justify-center items-center gap-3 max-md:text-sm text-black font-medium p-3 md:p-4 hover:bg-[#D5AA00] transition mb-4"
              >
                <img src={getQr} alt="getQr" className="w-5 mt-0.5" />
                Get QR Code
              </button>

              <div className="flex flex-col lg:flex-row gap-4">
                {/* Create Waiver */}
                <button 
                  onClick={() => navigate("/business/waiverbuilder/create")} 
                  className="w-full bg-transparent border border-foreground rounded-full inline-flex justify-center items-center gap-3 max-md:text-sm text-darkLighted font-medium capitalize p-3 md:p-4 hover:bg-gray-100 transition" 
                >
                  <img src={create} alt="create-add" className="size-5" />
                  Create Waiver
                </button>

                {/* View Signed Waivers */}
                <button 
                  onClick={() => navigate("/business/waivers/signatures")} 
                  className="w-full bg-transparent border border-foreground rounded-full inline-flex justify-center items-center gap-3 max-md:text-sm text-darkLighted font-medium capitalize p-3 md:p-4 hover:bg-gray-100 transition">
                  <img src={eye} alt="create-add" className="size-5" />
                  View Signed Waivers
                </button>
              </div>
            </div>

            {/* Recent Activity 
            <div className="bg-white border border-stroked rounded-2xl p-4 xs:p-5 md:p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="flex-none size-10 bg-[#F7F7F5] rounded-14 flex justify-center items-center">
                    <img src={ra} alt="ra" className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-xs xs:text-sm md:text-base text-darkLighted">
                    Recent Activity
                  </h3>
                </div>
                <div>
                  <button className="font-normal text-xs xs:text-sm md:text-base text-darkLighted hover:underline">
                    View all
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex max-xs:flex-col xs:items-center justify-between gap-3 pb-3 mb-3 border-b border-stroked last:mb-0 last:pb-0 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <div className="flex-none size-10 bg-[#F7F7F5] rounded-14 flex justify-center items-center">
                      <img src={ra1} alt="ra1" className="w-5 h-5" />
                    </div>
                    <p className="text-foreground font-normal text-sm md:text-base">
                      <span className="text-darkLighted font-medium">
                        John D
                      </span>
                      . signed{" "}
                      <span className="text-darkLighted font-medium">
                        Rock Climbing Waiver
                      </span>
                    </p>
                  </div>
                  <span className="flex-none text-foreground font-normal text-sm">
                    2 min ago
                  </span>
                </div>
                <div className="flex max-xs:flex-col xs:items-center justify-between gap-3 pb-3 mb-3 border-b border-stroked last:mb-0 last:pb-0 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <div className="flex-none size-10 bg-[#F7F7F5] rounded-14 flex justify-center items-center">
                      <img src={ra2} alt="ra2" className="w-5 h-5" />
                    </div>
                    <p className="text-foreground font-normal text-sm md:text-base">
                      <span className="text-darkLighted font-medium">
                        Sarah M.
                      </span>
                      . flagged for review
                    </p>
                  </div>
                  <span className="flex-none text-foreground font-normal text-sm">
                    2 min ago
                  </span>
                </div>
                <div className="flex max-xs:flex-col xs:items-center justify-between gap-3 pb-3 mb-3 border-b border-stroked last:mb-0 last:pb-0 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <div className="flex-none size-10 bg-[#F7F7F5] rounded-14 flex justify-center items-center">
                      <img src={ra3} alt="ra3" className="w-5 h-5" />
                    </div>
                    <p className="text-foreground font-normal text-sm md:text-base">
                      QR downloaded for{" "}
                      <span className="text-darkLighted font-medium">
                        Front Desk
                      </span>
                    </p>
                  </div>
                  <span className="flex-none text-foreground font-normal text-sm">
                    2 min ago
                  </span>
                </div>
                <div className="flex max-xs:flex-col xs:items-center justify-between gap-3 pb-3 mb-3 border-b border-stroked last:mb-0 last:pb-0 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <div className="flex-none size-10 bg-[#F7F7F5] rounded-14 flex justify-center items-center">
                      <img src={ra4} alt="ra4" className="w-5 h-5" />
                    </div>
                    <p className="text-foreground font-normal text-sm md:text-base">
                      New waiver created:{" "}
                      <span className="text-darkLighted font-medium">
                        CrossFit Member Agreement
                      </span>
                    </p>
                  </div>
                  <span className="flex-none text-foreground font-normal text-sm">
                    2 min ago
                  </span>
                </div>
              </div>
            </div>
            */}


            {/* Recent Activity */}
            <div className="bg-white border border-stroked rounded-2xl p-4 xs:p-5 md:p-6">

              <div className="flex items-center justify-between mb-6">

                <div className="flex items-center gap-3">
                  <div className="flex-none size-10 bg-[#F7F7F5] rounded-14 flex justify-center items-center">
                    <img
                      src={ra}
                      alt="Recent activity"
                      className="w-5 h-5"
                    />
                  </div>

                  <h3 className="font-semibold text-xs xs:text-sm md:text-base text-darkLighted">
                    Recent Activity
                  </h3>
                </div>

                <button
                  onClick={() => navigate("/business/recent-activity")}
                  className="font-normal text-xs xs:text-sm md:text-base text-darkLighted hover:underline"
                >
                  View all
                </button>

              </div>


              {/* Loading */}
              {recentActivityLoading && (
                <div className="py-6 text-center text-sm text-foreground">
                  Loading recent activity...
                </div>
              )}


              {/* Error */}
              {!recentActivityLoading && recentActivityError && (
                <div className="py-6 text-center text-sm text-red-500">
                  Unable to load recent activity.
                </div>
              )}


              {/* Empty */}
              {!recentActivityLoading &&
                !recentActivityError &&
                recentActivities.length === 0 && (
                  <div className="py-6 text-center text-sm text-foreground">
                    No recent activity.
                  </div>
                )}


              {/* Activities */}
              {!recentActivityLoading &&
                !recentActivityError &&
                recentActivities.length > 0 && (

                  <div className="space-y-3">

                    {recentActivities.map((activity) => (
                      <RecentActivityRow
                        key={activity._id}
                        activity={activity}
                      />
                    ))}


                  </div>

                )}

            </div>
          </div>
        </div>
      </div>

      {/* popup */}
      {/*showQRModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div>
            {/* <h1 className="text-3xl font-bold mb-2 text-gray-800 text-center">
              Hello, {business.fullName}
            </h1>
            <p className="text-gray-600 mb-6 text-center">
              Welcome to your business dashboard for{" "}
              <strong>{business.businessName}</strong>
            </p> * /}

            <div className="bg-white p-6 !pt-10 relative rounded-xl shadow-md flex flex-col items-center w-full max-w-md">
              <h2 className="text-lg font-semibold mb-4">
                Digital Waiver QR Code
              </h2>

              <img
                src={qrCodeImage}
                alt="Digital Waiver QR Code"
                className="w-48 h-48 mb-4 object-contain"
              />

              {/* <p className="text-sm text-gray-500 break-words text-center mb-2">
                Scan this code or visit the link below:
              </p>

              <a
                href={business.qrCodeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline break-words text-center mb-4"
              >
                {business.qrCodeUrl}
              </a> * /}

              <button
                onClick={handleDownload}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
              >
                Download High-Resolution QR Code
              </button>
              <button
                onClick={handleDownloadPDF}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition mt-2"
              >
                Download Brandable PDF
              </button>

              {/* cancle btn * /}
              <div className="absolute top-2 right-2">
                <button
                  onClick={() => setShowQRModal(false)}
                  className="bg-gray-200 rounded-md size-8 flex justify-center items-center"
                >
                  <img src={create} alt="create" className="size-6 rotate-45" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )*/}
    </div>
  );
};

export default BusinessDashboard;
